/**
 * interviewCache — a small, SESSION-SCOPED memo of the reads the track hub
 * opens with (F28 PR3), plus the drafts a mock in progress keeps between tab
 * changes. A track card's hover or focus prefetches the hub the click will
 * need; the hub, once landed, prefetches the guides; so the Overview and the
 * Learn tab open without a spinner ("preload everything").
 *
 * THE RULES
 *   - One promise per key, shared: a prefetch in flight and the foreground
 *     load that follows it are the SAME request, never two.
 *   - A failure is never cached. A background prefetch that meets a gate
 *     (401 / 403 / 426 / 404) evicts itself, and the foreground load
 *     re-requests WITHOUT the background tag — so the gate surfaces where
 *     the student is, and never through a request they did not ask for.
 *     That holds for a click that JOINS a prefetch still in flight too: the
 *     global 401 interceptor leaves a background request alone (it never
 *     signs the student out), so a foreground wait on a background request
 *     re-asks untagged when that request fails.
 *   - Everything dies with the session: `clearInterviewCache()` runs from
 *     `App.handleLogout` with the other shared-device wipes — a guardian
 *     acting for two minors, or two students on one machine, never see each
 *     other's hub (the hub carries the caller's own `active_mock`) nor each
 *     other's mock drafts.
 *   - The hub is invalidated by the writes that change it (a mock started or
 *     finished): `invalidateTrackHub`.
 *
 * MOCK DRAFTS: a tab change unmounts the mock runner (every tab is a URL), so
 * the answers a student has typed but not saved live here, keyed by session,
 * until the mock is finished or the session ends.
 *
 * Module-level by design: the memo must outlive the route (the picker
 * prefetches for a hub that is not mounted yet) and die with the session.
 */
import { getTrackHub, listTrackGuides } from './interviewApi';

const entries = new Map(); // key → { promise, background }
const mockDrafts = new Map(); // sessionId → { questionId: text }

function scopedStudent(studentId) {
  return studentId ? String(studentId) : '';
}

export function hubKey(slug, studentId) {
  return `hub:${slug}:${scopedStudent(studentId)}`;
}

export function guidesKey(slug, studentId) {
  return `guides:${slug}:${scopedStudent(studentId)}`;
}

/** Forget everything — the logout wipe. */
export function clearInterviewCache() {
  entries.clear();
  mockDrafts.clear();
}

/** True when `key` has a load in flight or landed (never a failed one). */
export function isCached(key) {
  return entries.has(key);
}

/**
 * The shared promise for `key`: an existing load, or `loader()` started now.
 * A rejection evicts the entry so the next call tries again.
 *
 * `background` marks a prefetch. A FOREGROUND call that finds a background
 * request still in flight waits on it, and if that request fails it asks
 * again with its own (untagged) loader — the student is waiting on this one,
 * so its failure must be a foreground failure.
 */
export function cachedLoad(key, loader, { background = false } = {}) {
  const hit = entries.get(key);
  if (hit) {
    if (background || !hit.background) return hit.promise;
    return hit.promise.catch(() => cachedLoad(key, loader));
  }
  let promise;
  try {
    promise = Promise.resolve(loader());
  } catch (err) {
    promise = Promise.reject(err);
  }
  const entry = { promise, background };
  entries.set(key, entry);
  promise.then(
    () => {
      // Landed: a later foreground read is served as-is, tag or no tag.
      if (entries.get(key) === entry) entry.background = false;
    },
    () => {
      if (entries.get(key) === entry) entries.delete(key);
    },
  );
  return promise;
}

export function invalidate(key) {
  entries.delete(key);
}

// ── The two reads the hub is built on ────────────────────────────────────────

/** The hub payload for `slug` — cached; a foreground (untagged) read. */
export function loadTrackHub(slug, studentId) {
  return cachedLoad(hubKey(slug, studentId), () => getTrackHub(slug, studentId));
}

/**
 * Warm the hub for `slug` in the background (a track card hover / focus).
 * Never throws and never surfaces a gate: a failure evicts itself and the
 * click's foreground load asks again.
 */
export function prefetchTrackHub(slug, studentId) {
  const key = hubKey(slug, studentId);
  if (entries.has(key)) return;
  cachedLoad(key, () => getTrackHub(slug, studentId, { background: true }), { background: true })
    .catch(() => {});
}

/** A track's guides (with bodies) — cached; a foreground read. */
export function loadTrackGuides(slug, studentId) {
  return cachedLoad(guidesKey(slug, studentId), () => listTrackGuides(slug, studentId));
}

/** Warm the guides in the background (right after the hub lands). */
export function prefetchTrackGuides(slug, studentId) {
  const key = guidesKey(slug, studentId);
  if (entries.has(key)) return;
  cachedLoad(key, () => listTrackGuides(slug, studentId, { background: true }), { background: true })
    .catch(() => {});
}

/** Forget the hub for `slug` — after a write that changes it (a mock started
 * or finished changes `active_mock`; an answer changes nothing the hub shows). */
export function invalidateTrackHub(slug, studentId) {
  entries.delete(hubKey(slug, studentId));
}

/** Forget the guides for `slug` — a retry after a failed or incomplete read. */
export function invalidateTrackGuides(slug, studentId) {
  entries.delete(guidesKey(slug, studentId));
}

// ── Mock drafts ──────────────────────────────────────────────────────────────

/** The unsaved answers of a mock session, `{ questionId: text }` (a copy). */
export function getMockDrafts(sessionId) {
  return { ...(mockDrafts.get(sessionId) || {}) };
}

/** Remember (or, with empty text, forget) one unsaved answer. */
export function setMockDraft(sessionId, questionId, text) {
  const drafts = mockDrafts.get(sessionId) || {};
  if (text) drafts[questionId] = text;
  else delete drafts[questionId];
  if (Object.keys(drafts).length > 0) mockDrafts.set(sessionId, drafts);
  else mockDrafts.delete(sessionId);
}

/** Forget a session's drafts — the mock is finished or closed. */
export function clearMockDrafts(sessionId) {
  mockDrafts.delete(sessionId);
}
